import { PopoverProperty } from '@/components/common/propertyPopoverOverlay/Property.tsx'
import { createLabelGroupProperty } from '@/routes/projectStages/issues/lib/hooks/useCreateLabelGroupsProperties.tsx'
import { IssueTypeT } from '@/components/common/menu/lib/types/navigationDataTypes.ts'
import { useTranslation } from 'react-i18next'
import { MapIcon, TagIcon } from 'lucide-react'
import { createStatusGroupProperty } from '@/routes/projectStages/issues/lib/issuesHelpers/createStatusGroupProperty.tsx'
import createUsersProperties from '@/routes/projectStages/issues/lib/issuesHelpers/createUsersProperties.tsx'
import { useCallback, useEffect } from 'react'
import { ProjectStagesUsersT } from '@/components/common/menu/lib/types/projectStagesUsers.ts'
import { useProjectStageStore } from '@/components/common/menu/lib/store/useProjectStageStore.tsx'
import { Dot } from '@/assets/icons/dot.tsx'
import { Done } from '@/assets/icons/statusIcons.tsx'
import useSelectAllProperty from '@/lib/hooks/useSelectAllProperty.tsx'
import { createCustomFilterProperties } from '@/routes/projectStages/issues/lib/issuesHelpers/createCustomFiltersProperties.tsx'
import {
  ProjectStageFiltersT,
  useProjectStageFiltersStore,
} from '@/components/common/menu/lib/store/useProjectStageFilters.ts'
import {
  createdAtProperty,
  deadlineProperty,
  updatedAtProperty,
  recipientsProperty,
  clearFiltersProperty,
  creatorsProperty,
} from '@/routes/root/lib/staticProperties.tsx'
import useCreatePropertiesByIssueType from '@/routes/projectStages/issues/lib/hooks/useCreatePropertiesByIssueType.tsx'
import useCreateLocationsFilters from '@/routes/root/lib/hooks/useCreateLocationsFilters.tsx'
import createSeparator from '@/components/common/propertyPopoverOverlay/createSeparator.tsx'
import useCreateStagesProps from '@/routes/root/lib/hooks/useCreateStagesProps.tsx'

export default function useCreateStageFilters(
  users: ProjectStagesUsersT | null | undefined,
  stageId: string | number | null | undefined,
) {
  const { t } = useTranslation()

  const issueTypes = useProjectStageStore(state => state.issueTypes)
  const setFilters = useProjectStageFiltersStore(state => state.setFilters)

  const selectAllProperty = useSelectAllProperty(`all`)
  const { usersProperties } = createUsersProperties(users)
  const recipients = createUsersProperties(users, `recipients`).usersProperties
  const creators = createUsersProperties(users, `creators`).usersProperties

  const recipientsWithSelectAll: PopoverProperty[] = [
    selectAllProperty,
    createSeparator(),
    ...recipients,
  ]

  const creatorsWithSelectAll: PopoverProperty[] = [
    selectAllProperty,
    createSeparator(),
    ...creators,
  ]

  recipientsProperty.children = recipientsWithSelectAll
  creatorsProperty.children = creatorsWithSelectAll

  const stagesProperties = useCreateStagesProps()

  const createPropertiesFromIssueTypesData = useCallback(() => {
    const issueTypesPropertyChildren: PopoverProperty[] = []

    const statusesArr: PopoverProperty[][] = []
    const statusesPropertiesArr: PopoverProperty[] = []
    const labelGroups: PopoverProperty[] = []
    const customFilters: PopoverProperty[] = []

    issueTypes.forEach((issueType: IssueTypeT) => {
      const { statuses } = createStatusGroupProperty(issueType)
      statuses.unshift(selectAllProperty)

      const currentUserCustomFilters = issueType.currentUserCustomFilters
      const customFiltersProperties = createCustomFilterProperties(currentUserCustomFilters)

      if (customFiltersProperties.length > 0) {
        customFilters.push(...customFiltersProperties)
      }

      // console.log(issueType.statusGroup)
      if (issueType.statusGroup) {
        statusesArr.push(statuses)

        const statusGroupNestedProperty: PopoverProperty = {
          label: issueType.statusGroup.description ?? '',
          value: issueType.statusGroup['@id'] ?? '/statuses',
          icon: statuses[1]?.icon ?? <Done className={`size-4`} />,
          children: statuses,
        }

        statusesPropertiesArr.push(statusGroupNestedProperty)
      }

      issueType?.labelGroups?.forEach(labelGroup => {
        if (labelGroups.flat().some(labelG => labelG.value === labelGroup['@id'])) return
        const labelGroupProp = createLabelGroupProperty(labelGroup)
        labelGroupProp.children?.unshift(selectAllProperty)
        labelGroups.push(labelGroupProp)
      })

      // create issueType single property
      const issueTypeProperty: PopoverProperty = {
        label: issueType.name,
        value: issueType?.['@id'] ?? '',
        icon: <Dot fill={issueType.color} />,
      }
      issueTypesPropertyChildren.push(issueTypeProperty)
    })

    // create issue types property
    const issueTypesGroupProperty: PopoverProperty = {
      label: t(`archive.issueType`),
      value: 'issue_types',
      icon: <TagIcon className={`size-4`} />,
      children: [selectAllProperty, ...issueTypesPropertyChildren],
    }

    return { statusesPropertiesArr, labelGroups, issueTypesGroupProperty, customFilters }
  }, [issueTypes, selectAllProperty, t])

  const locationsProperties = useCreateLocationsFilters(stageId, selectAllProperty).locations

  const selectAllLocations: PopoverProperty = {
    ...selectAllProperty,
    value: `all-locations-top-level`,
  }

  const locationProperty: PopoverProperty = {
    label: t(`archive.location`),
    value: 'locations',
    parent: 'locations',
    icon: <MapIcon className={`size-4`} />,
    children: [selectAllLocations, createSeparator(), ...locationsProperties],
  }

  const createStageFilters = useCallback(() => {
    if (!issueTypes) return

    const { labelGroups, statusesPropertiesArr, issueTypesGroupProperty } =
      createPropertiesFromIssueTypesData()

    const reportsFilters: PopoverProperty[] = [
      creatorsProperty,
      createSeparator(),
      createdAtProperty,
      createSeparator(),
      clearFiltersProperty,
    ]
    const archiveFilters: PopoverProperty[] = [
      // ...customFilters,
      // createPopoverProperty('separator', 'separator0'),

      issueTypesGroupProperty,
      createSeparator(),
      creatorsProperty,
      recipientsProperty,
      createSeparator(),
      ...labelGroups,
      createSeparator(),
      ...statusesPropertiesArr,
      createSeparator(),
      locationProperty,
      createSeparator(),

      deadlineProperty,
      createdAtProperty,
      updatedAtProperty,

      createSeparator(),
      clearFiltersProperty,
    ]

    const notificationsFilters: PopoverProperty[] = [
      // ...customFilters,
      // createPopoverProperty('separator', 'separator0'),

      issueTypesGroupProperty,
      createSeparator(),
      creatorsProperty,
      recipientsProperty,
      createSeparator(),
      ...labelGroups,
      createSeparator(),
      ...statusesPropertiesArr,
      createSeparator(),
      locationProperty,
      createSeparator(),

      deadlineProperty,
      createdAtProperty,
      updatedAtProperty,

      createSeparator(),
      clearFiltersProperty,
    ]

    const allFilters: ProjectStageFiltersT = {
      stagesProperties,
      archiveProps: archiveFilters,
      reportsFilters,
      notificationsFilters: notificationsFilters,
      users: usersProperties,
      issueTypeProperty: issueTypesGroupProperty,
      statusGroupsProperties: statusesPropertiesArr,
      labelGroupsProperties: labelGroups,
      locationProperties: locationsProperties,
    }

    return allFilters
  }, [
    stagesProperties,
    createPropertiesFromIssueTypesData,
    issueTypes,
    locationProperty,
    locationsProperties,
    usersProperties,
  ])

  useCreatePropertiesByIssueType(
    issueTypes,
    stageId,
    selectAllProperty,
    creatorsProperty,
    recipientsProperty,
    clearFiltersProperty,
    locationProperty,
  )

  useEffect(() => {
    const filters = createStageFilters()
    if (!filters) return
    setFilters(filters)
  }, [createStageFilters, setFilters])

  return {
    recipientsProperty,
    creatorsProperty,
    selectAllProperty,
    clearFiltersProperty,
  }
}
